import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { CheckCircle2, Loader2, AlertTriangle, Sparkles, ExternalLink } from "lucide-react";

/**
 * PublicValidatePage — Phase 24
 * URL publique : /valider/:slug   (ex: /valider/pizza-roma)
 *
 * Page sans header/footer applicatif. Conçue pour être envoyée au client final
 * pour qu'il valide la livraison de son site. Une fois validé : licence démarrée,
 * site marqué comme "livré" côté HUB.
 */
export default function PublicValidatePage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validatedNow, setValidatedNow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get(`/agency/public-license/by-slug/${encodeURIComponent(slug || "")}/validation-status`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || "Lien de validation introuvable"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!accepted) return;
    setSubmitting(true);
    setError("");
    try {
      const r = await api.post(`/agency/public-license/by-slug/${encodeURIComponent(slug)}/client-validate`, {
        accepted: true,
        validator_email: email.trim(),
      });
      setData(d => ({ ...d, validated: true, validated_at: r.data.validated_at, license_started_at: r.data.license_started_at }));
      setValidatedNow(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de valider — veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const primaryColor = data?.primary_color || "#0F2744";

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
      style={{
        background: `radial-gradient(circle at 20% 0%, ${primaryColor}18, transparent 60%), radial-gradient(circle at 100% 100%, ${primaryColor}12, transparent 50%), #f7f8fb`,
      }}
      data-testid="public-validate-page"
    >
      {/* Agency branding bar */}
      {data?.agency && (
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-xs text-slate-500" data-testid="agency-branding-bar">
          <div className="flex items-center gap-2">
            {data.agency.logo_url && (
              <img src={data.agency.logo_url} alt="" className="w-7 h-7 rounded-full object-cover bg-white border border-slate-200" />
            )}
            <span className="font-semibold text-slate-700">{data.agency.name}</span>
          </div>
          {data.agency.email && <a className="hover:text-slate-700" href={`mailto:${data.agency.email}`}>{data.agency.email}</a>}
        </div>
      )}

      {loading && (
        <div className="text-slate-500 flex items-center gap-2" data-testid="public-validate-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
          Chargement de votre fiche…
        </div>
      )}

      {!loading && error && (
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="public-validate-error">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold mb-2">Lien introuvable</h2>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-400 mt-4">Vérifiez le lien fourni par votre agence.</p>
        </div>
      )}

      {!loading && data && !error && (
        <div className="bg-white rounded-3xl shadow-xl max-w-xl w-full overflow-hidden" data-testid="public-validate-card">
          <div
            className="px-8 py-6 text-white relative"
            style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
          >
            <div className="text-xs uppercase tracking-widest opacity-80 mb-1">Validation de livraison</div>
            <div className="text-2xl font-bold leading-tight" data-testid="company-name">
              {data.company_name || data.site_label || "Votre site COCKPIT"}
            </div>
            {data.sector && <div className="text-xs opacity-80 mt-1 capitalize">{data.sector}</div>}
            {data.logo_url && (
              <img src={data.logo_url} alt="" className="absolute right-6 top-6 w-14 h-14 rounded-xl object-cover bg-white/20 border border-white/30" />
            )}
          </div>

          <div className="p-8">
            {data.validated ? (
              <div className="text-center py-4" data-testid="already-validated-block">
                <CheckCircle2 className={`w-14 h-14 mx-auto mb-3 ${validatedNow ? "text-emerald-500 animate-bounce" : "text-emerald-500"}`} />
                <h2 className="text-xl font-bold mb-1">
                  {validatedNow ? "Merci, livraison validée !" : "Site déjà validé"}
                </h2>
                <p className="text-sm text-slate-600 mb-4">
                  {validatedNow
                    ? "Votre site est officiellement en ligne et votre licence démarre aujourd'hui. Notre équipe va vous contacter pour vous transmettre vos accès."
                    : `Validé le ${new Date(data.validated_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}.`}
                </p>
                {data.site_url && (
                  <a
                    href={data.site_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white"
                    style={{ background: primaryColor }}
                    data-testid="visit-site-link"
                  >
                    Voir mon site <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="validate-form">
                <p className="text-sm text-slate-600 leading-relaxed">
                  Bonjour ! Votre site est prêt. Avant la mise en ligne définitive,
                  nous avons besoin de votre accord. Veuillez lire le texte ci-dessous puis
                  cocher la case pour valider la livraison.
                </p>

                {data.site_url && (
                  <a
                    href={data.site_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block w-full text-center px-4 py-3 rounded-xl text-sm font-semibold border-2 hover:bg-slate-50 transition"
                    style={{ borderColor: primaryColor, color: primaryColor }}
                    data-testid="preview-site-link"
                  >
                    Prévisualiser mon site <ExternalLink className="w-3.5 h-3.5 inline -mt-0.5" />
                  </a>
                )}

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-700 leading-relaxed" data-testid="legal-text">
                  {data.legal_text}
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Votre email (optionnel)
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="vous@exemple.fr"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-slate-400 text-sm"
                    data-testid="validator-email-input"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-xl bg-amber-50 border border-amber-200 hover:bg-amber-100 transition" data-testid="accept-checkbox-label">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={e => setAccepted(e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-amber-600 cursor-pointer"
                    data-testid="accept-checkbox"
                  />
                  <span className="text-xs font-medium text-amber-900 leading-relaxed">
                    J'ai lu et j'accepte les conditions de validation. Je confirme que mon site est livré conforme.
                  </span>
                </label>

                {error && (
                  <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="form-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!accepted || submitting}
                  className="w-full py-3 rounded-xl text-sm font-bold text-white shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ background: primaryColor }}
                  data-testid="submit-validate-button"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {submitting ? "Validation en cours…" : "Valider la livraison de mon site"}
                </button>

                <p className="text-[11px] text-slate-400 text-center">
                  Ce lien est strictement réservé à votre site. Aucune information n'est partagée publiquement.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
